import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';

// ============================================
// Types
// ============================================

export type RuleScopeType =
  | 'traffic_source'
  | 'flow'
  | 'offer'
  | 'network'
  | 'landing_page'
  | 'dimension'
  | 'global';

export type RuleMetric =
  | 'visits'
  | 'unique_visits'
  | 'clicks'
  | 'unique_clicks'
  | 'conversions'
  | 'revenue'
  | 'cost'
  | 'profit'
  | 'roi'
  | 'roas'
  | 'epv'
  | 'cpv'
  | 'cr'
  | 'ctr'
  | 'cv_rate'
  | 'rpl'
  | 'cpl';

export type RuleOperator = '>' | '>=' | '<' | '<=' | '==' | '!=';

export type TimeframeWindow = 'last_hours' | 'last_days' | 'today' | 'yesterday';

export interface RuleTimeframe {
  window: TimeframeWindow;
  value?: number;
}

// --- Condition tree ---

export interface ThresholdLeaf {
  type: 'threshold';
  metric: RuleMetric;
  operator: RuleOperator;
  value: number;
}

export interface ComparisonLeaf {
  type: 'comparison';
  metric: RuleMetric;
  operator: RuleOperator;
  rhs_metric: RuleMetric;
}

export interface PeriodLeaf {
  type: 'period';
  metric: RuleMetric;
  operator: RuleOperator;
  compare_to: 'previous';
  factor?: number;
}

export type ConditionLeaf = ThresholdLeaf | ComparisonLeaf | PeriodLeaf;

export interface ConditionGroup {
  op: 'AND' | 'OR';
  conditions: Array<ConditionLeaf | ConditionGroup>;
}

export function isConditionGroup(
  node: ConditionLeaf | ConditionGroup
): node is ConditionGroup {
  return (node as ConditionGroup).op !== undefined;
}

// --- Actions ---

export interface RuleAction {
  type: 'notify';
  channel_id: string;
}

// --- Rule ---

export type RuleStatus = 'active' | 'archived' | string;

export interface Rule {
  id: string;
  workspace_id: string;
  name: string;
  enabled: boolean;
  status: RuleStatus;
  scope_type: RuleScopeType;
  dimension: string | null;
  filters: Record<string, unknown>;
  timeframe: RuleTimeframe;
  exclude_hours: number;
  frequency_minutes: number;
  cooldown_hours: number;
  conditions: ConditionGroup;
  actions: RuleAction[];
  last_evaluated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface RuleEvaluation {
  id: string;
  rule_id: string;
  entity_id: string;
  entity_label: string;
  matched: boolean;
  action_taken: string;
  metrics?: Record<string, number>;
  evaluated_at: string;
}

export interface RuleTestEntity {
  entity_id: string;
  entity_label: string;
  metrics: Record<string, number>;
}

export interface RuleTestResult {
  matched: number;
  entities: RuleTestEntity[];
}

export interface CreateRuleData {
  name: string;
  enabled: boolean;
  scope_type: RuleScopeType;
  dimension: string | null;
  filters: Record<string, unknown>;
  timeframe: RuleTimeframe;
  exclude_hours: number;
  frequency_minutes: number;
  cooldown_hours: number;
  conditions: ConditionGroup;
  actions: RuleAction[];
}

export type UpdateRuleData = Partial<CreateRuleData>;

// ============================================
// Service
// ============================================

export const rulesService = {
  query: async (filters: { limit?: number; offset?: number; where?: WhereCondition } = {}): Promise<{ data: Rule[]; total: number }> => {
    const payload: QueryPayload = {
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 500,
      order_by: 'rule.created_at',
      order: 'desc',
      ...(filters.where && { where: filters.where }),
    };
    const response = await api.post<QueryResponse<Rule>>('/rules/query', payload);
    return { data: response.data.data, total: response.data._meta?.total ?? 0 };
  },

  list: async (includeArchived = false): Promise<Rule[]> => {
    const response = await api.get<ApiResponse<Rule[]>>('/rules', {
      params: includeArchived ? { include_archived: '1' } : undefined,
    });
    return response.data.data || [];
  },

  unarchive: async (id: string): Promise<void> => {
    await api.post(`/rules/${id}/unarchive`);
  },

  getById: async (id: string): Promise<Rule> => {
    const response = await api.get<ApiResponse<Rule>>(`/rules/${id}`);
    return response.data.data!;
  },

  create: async (data: CreateRuleData): Promise<Rule> => {
    const response = await api.post<ApiResponse<Rule>>('/rules', data);
    return response.data.data!;
  },

  update: async (id: string, data: UpdateRuleData): Promise<Rule> => {
    const response = await api.patch<ApiResponse<Rule>>(`/rules/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/rules/${id}`);
  },

  test: async (id: string): Promise<RuleTestResult> => {
    const response = await api.post<ApiResponse<RuleTestResult>>(`/rules/${id}/test`);
    return response.data.data!;
  },

  getEvaluations: async (
    id: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<RuleEvaluation[]> => {
    const response = await api.get<ApiResponse<RuleEvaluation[]>>(
      `/rules/${id}/evaluations`,
      { params }
    );
    return response.data.data || [];
  },
};
